import { useCallback, useMemo, useRef } from 'react';
import I18n from 'I18n';
import UIInlineLabel from 'UIComponents/form/UIInlineLabel';
import UsersAndTeamsSelect from 'ui-asset-management-lib/components/tableFilters/UsersAndTeamsSelect';
import { getAllChildTeamIdsForParentTeamId } from 'ui-asset-management-lib/utils/TeamUtils';
import useFetchTeams from 'framework-listing-lib/internal/hooks/useFetchTeams';
import useFetchUsers from 'framework-listing-lib/internal/hooks/useFetchUsers';
import useCrmObjectTypeTracking from 'framework-listing-lib/internal/hooks/useCrmObjectTypeTracking';
import { SELECTION_ID_ALL, SELECTION_ID_ME, SELECTION_TYPE_CUSTOM, USERS_PROPERTY_NAME, TEAMS_PROPERTY_NAME, ALL_TEAMS_OPTION, MY_TEAMS_OPTION, ME_OPTION, SELECTION_ID_MY_TEAMS } from 'framework-listing-lib/constants/access';
import { HAS_PROPERTY, IN } from 'framework-listing-lib/utils/filters';
import useCrmObjectTypeName from 'framework-listing-lib/internal/hooks/useCrmObjectTypeName';
import useUserInfo from 'framework-listing-lib/internal/hooks/useUserInfo';
import ListingLibFilterWrapper from 'framework-listing-lib/filters/ListingLibFilterWrapper';
import SelectButtonContent from 'framework-listing-lib/internal/FilterBar/components/SelectButtonContent';
import { DEFAULT_USE, getMyTeamIds, getTeamIdsFromChangeEvent, getUserIdsFromChangeEvent, getSelectedValueFromFilters, teamsAreFilterable, usersAreFilterable, getAllTeamIds, isTeamOption, isUserOption, handleDeselectParentTeam, MULTI_TEAM_ACTIONS, getMultiTeamAction, getMultiUserAction, MULTI_USER_ACTIONS } from 'framework-listing-lib/utils/accessUtils';
import { useListingLibQuickFilters } from 'framework-listing-lib/hooks/useQuickFilters';
import { jsx as _jsx } from "react/jsx-runtime";
export default function ListingLibUsersAndTeamsSelect({
  disabled,
  multi,
  viewAllAssetsScope,
  viewTeamOwnedAssetsScope,
  use = DEFAULT_USE
}) {
  const {
    data: users,
    loading: loadingUsers,
    error: errorLoadingUsers
  } = useFetchUsers({
    viewAllAssetsScope,
    viewTeamOwnedAssetsScope
  });
  const {
    data: teams,
    loading: loadingTeams,
    error: errorLoadingTeams
  } = useFetchTeams({
    viewAllAssetsScope,
    viewTeamOwnedAssetsScope
  });

  /**
   * Preserve @multi value when set (true / false)
   * Otherwise:
   *   - make multi true when @use is button: https://git.hubteam.com/HubSpot/CampaignsUXTaskForce/issues/7
   */
  const isMulti = useMemo(() => {
    if (multi !== undefined) {
      return multi;
    }
    return use === 'button';
  }, [multi, use]);

  /**
   * Truthy when user selects "All users and teams"
   */
  const selectAllRef = useRef(false);
  /**
   * Truthy when user selects "My teams"
   */
  const selectMyTeamsRef = useRef(false);
  /**
   * Truthy when user "Me" option
   */
  const selectMeRef = useRef(false);
  const {
    getQuickFilterValue,
    quickFilters,
    removeQuickFilter,
    setQuickFilter
  } = useListingLibQuickFilters();
  const {
    trackCrmTableInteraction
  } = useCrmObjectTypeTracking();
  const {
    userInfo
  } = useUserInfo();
  const loadingUserInfo = !userInfo;
  const myUserId = (userInfo === null || userInfo === void 0 ? void 0 : userInfo.user.user_id) || 0;
  const myTeamIds = useMemo(() => getMyTeamIds(userInfo), [userInfo]);
  const allTeamIds = useMemo(() => getAllTeamIds(teams), [teams]);
  const value = useMemo(() => {
    const selectedTeamIds = getQuickFilterValue(TEAMS_PROPERTY_NAME, []);
    const selectedUserIds = getQuickFilterValue(USERS_PROPERTY_NAME, []);
    const hasAllTeamsOption = quickFilters.some(filter => filter.operator === 'HAS_PROPERTY' && filter.property === TEAMS_PROPERTY_NAME);
    const hasAllUsersOption = quickFilters.some(filter => filter.operator === 'HAS_PROPERTY' && filter.property === USERS_PROPERTY_NAME);
    if (isMulti) {
      const valueForUsers = getSelectedValueFromFilters({
        allUsers: users,
        hasAllUsersOption,
        isMulti,
        myUserId,
        selectedUserIds
      });
      const valueForTeams = getSelectedValueFromFilters({
        allTeams: teams,
        hasAllTeamsOption,
        isMulti,
        myTeamIds,
        selectedTeamIds
      });
      let result = Array.from(new Set([...valueForUsers, ...valueForTeams]));

      /**
       * Uses "ALL" option only when both all teams and users are selected
       */
      if (result.includes(SELECTION_ID_ALL) && (!valueForUsers.includes(SELECTION_ID_ALL) || !valueForTeams.includes(SELECTION_ID_ALL))) {
        result = result.filter(val => val !== SELECTION_ID_ALL);
      }

      /**
       * Uses "Me" only when user id is present without any other
       * team or user id
       */
      if (result.includes(SELECTION_ID_ME) && result.filter(val => val !== SELECTION_ID_ME && val !== myUserId).length) {
        result = result.filter(val => val !== SELECTION_ID_ME);
      }

      /**
       * Uses "My team" only if user's team ids are selected
       */
      const hasOtherTeams = valueForTeams.filter(val => val !== SELECTION_ID_MY_TEAMS && typeof val === 'number' && !myTeamIds.includes(val)).length > 0;
      const hasAnyUser = valueForUsers.length > 0;
      if (result.includes(SELECTION_ID_MY_TEAMS) && (hasOtherTeams || hasAnyUser)) {
        result = result.filter(val => val !== SELECTION_ID_MY_TEAMS);
      }
      selectAllRef.current = Boolean(result.includes(SELECTION_ID_ALL));
      selectMeRef.current = Boolean(result.includes(SELECTION_ID_ME));
      selectMyTeamsRef.current = Boolean(result.includes(SELECTION_ID_MY_TEAMS));
      return result;
    }
    return getSelectedValueFromFilters({
      allTeams: teams,
      allUsers: users,
      hasAllTeamsOption,
      hasAllUsersOption,
      isMulti,
      myTeamIds,
      myUserId,
      selectedTeamIds,
      selectedUserIds
    });
  }, [getQuickFilterValue, isMulti, myTeamIds, myUserId, quickFilters, teams, users]);
  const handleChange = useCallback(changeValue => {
    let teamIds = [];
    let userIds = [];
    if (Array.isArray(changeValue)) {
      const newValue = handleDeselectParentTeam({
        prevValue: getQuickFilterValue(TEAMS_PROPERTY_NAME),
        newValue: changeValue,
        teams
      });
      const multiTeamAction = getMultiTeamAction({
        allTeamIds,
        myTeamIds,
        newValue,
        selectAllRefValue: selectAllRef.current,
        selectMyTeamsRefValue: selectMyTeamsRef.current
      });
      const multiUserAction = getMultiUserAction({
        myUserId,
        newValue,
        selectAllRefValue: selectAllRef.current,
        selectMeRefValue: selectMeRef.current,
        users
      });

      /**
       * Handle teams
       */
      if (multiTeamAction === MULTI_TEAM_ACTIONS.SELECT_MY_TEAMS) {
        for (const teamId of myTeamIds) {
          teamIds = [...teamIds, teamId, ...getAllChildTeamIdsForParentTeamId(teamId, teams)];
        }
      } else if (multiTeamAction === MULTI_TEAM_ACTIONS.SELECT_ALL_TEAMS) {
        // Setting @teamIds to undefined to apply the correct HAS_PROPERTY filter
        teamIds = undefined;
      } else if (multiTeamAction === MULTI_TEAM_ACTIONS.NONE && multiUserAction !== MULTI_USER_ACTIONS.DESELECT && multiUserAction !== MULTI_USER_ACTIONS.SELECT_ME) {
        /**
         * At this point, user is selecting individual team ids and we should only take into consideration "TEAM" options.
         * "My teams" and "All teams" options have been already handled in the conditions above.
         *
         * Handle team ids ONLY if user didn't select "All users" or "Me" option
         */
        for (const team of newValue) {
          if (!isTeamOption(team)) {
            continue;
          }
          const {
            id,
            type
          } = team;
          teamIds = [...teamIds, ...getTeamIdsFromChangeEvent(type, id, myTeamIds, teams)];
        }
      }

      /**
       * Handle users
       */
      if (multiUserAction === MULTI_USER_ACTIONS.SELECT_ME) {
        userIds = [myUserId];
      } else if (multiUserAction === MULTI_USER_ACTIONS.SELECT_ALL_USERS) {
        // Setting @userIds to undefined to apply the correct HAS_PROPERTY filter
        userIds = undefined;
      } else if (multiUserAction === MULTI_USER_ACTIONS.NONE && multiTeamAction !== MULTI_TEAM_ACTIONS.DESELECT && multiTeamAction !== MULTI_TEAM_ACTIONS.SELECT_MY_TEAMS) {
        /**
         * At this point, user is selecting individual user ids and we should only take into consideration "USER" options.
         * "Me" and "All users" options have been already handled in the conditions above.
         *
         * Handle user ids ONLY if user didn't select "All teams" or "My teams" options
         */
        for (const user of newValue) {
          if (!isUserOption(user)) {
            continue;
          }
          const {
            id,
            type
          } = user;
          userIds = [...userIds, ...getUserIdsFromChangeEvent(type, id, myUserId)];
        }
      }
    } else {
      const {
        id,
        type
      } = changeValue;
      userIds = getUserIdsFromChangeEvent(type, id, myUserId);
      teamIds = getTeamIdsFromChangeEvent(type, id, myTeamIds, teams);
    }
    trackCrmTableInteraction({
      action: 'filter-objects-by-user-and-team'
    });
    removeQuickFilter(USERS_PROPERTY_NAME);
    removeQuickFilter(TEAMS_PROPERTY_NAME);

    /**
     * Set @teamIds
     */

    if (teamIds === undefined) {
      setQuickFilter(HAS_PROPERTY(TEAMS_PROPERTY_NAME));
    }
    teamIds = teamIds !== undefined ? Array.from(new Set(teamIds)) : undefined;
    if (teamIds && teamIds.length) {
      setQuickFilter(IN(TEAMS_PROPERTY_NAME, teamIds));
    }

    /**
     * Set @userIds
     */

    if (userIds === undefined) {
      setQuickFilter(HAS_PROPERTY(USERS_PROPERTY_NAME));
    }
    userIds = userIds !== undefined ? Array.from(new Set(userIds)) : undefined;
    if (userIds && userIds.length) {
      setQuickFilter(IN(USERS_PROPERTY_NAME, userIds));
    }
  }, [allTeamIds, getQuickFilterValue, myTeamIds, myUserId, removeQuickFilter, setQuickFilter, teams, trackCrmTableInteraction, users]);
  const handleClearUsersAndTeams = useCallback(() => {
    selectAllRef.current = false;
    selectMyTeamsRef.current = false;
    selectMeRef.current = false;
    removeQuickFilter(USERS_PROPERTY_NAME);
    removeQuickFilter(TEAMS_PROPERTY_NAME);
  }, [removeQuickFilter]);

  /**
   * Avoid user or team sections that contain only a single entry.
   */
  const {
    shouldRender,
    usersToRender,
    teamsToRender,
    showMyTeams
  } = useMemo(() => {
    return {
      shouldRender: usersAreFilterable(users) || teamsAreFilterable(teams),
      usersToRender: usersAreFilterable(users) ? users : [],
      teamsToRender: teamsAreFilterable(teams) ? teams : [],
      showMyTeams: teamsAreFilterable(teams)
    };
  }, [users, teams]);
  const ButtonContent = useCallback(() => {
    const count = isMulti && Array.isArray(value) ? value.filter(option => option !== ALL_TEAMS_OPTION.id && option !== MY_TEAMS_OPTION.id && option !== ME_OPTION.id).length : 0;
    return /*#__PURE__*/_jsx(SelectButtonContent, {
      count: count,
      messageName: "frameworkListingLib.usersAndTeamsSelect.usersAndTeams",
      messageNameWithCount: "frameworkListingLib.usersAndTeamsSelect.usersAndTeamsWithCounts"
    });
  }, [isMulti, value]);
  const {
    objectTypeNameSingular
  } = useCrmObjectTypeName();
  if (!shouldRender || loadingTeams || loadingUsers || loadingUserInfo) {
    return null;
  }
  const selectComponent = /*#__PURE__*/_jsx(UsersAndTeamsSelect, {
    additionalOptions: [{
      type: SELECTION_TYPE_CUSTOM,
      text: I18n.text('frameworkListingLib.usersSelect.me'),
      value: SELECTION_ID_ME
    }, {
      type: SELECTION_TYPE_CUSTOM,
      text: I18n.text('frameworkListingLib.usersAndTeamsSelect.allUsersAndTeams'),
      value: SELECTION_ID_ALL
    }],
    allowPrivate: false,
    assetTypeDisplayName: objectTypeNameSingular,
    customTeamOptionDisabled: myTeamIds.length === 0,
    "data-test-id": "crm-listing-users-and-teams-select",
    disabled: disabled,
    onChange: handleChange,
    requestsError: Boolean(errorLoadingTeams || errorLoadingUsers),
    requestsLoading: loadingTeams || loadingUsers || loadingUserInfo,
    showAny: false,
    showUnassigned: false,
    showMyTeamsOption: showMyTeams,
    teams: teamsToRender,
    users: usersToRender,
    multi: isMulti
    // These props need to be passed down like this as the component
    // uses them in a rest operator and don't have types for them
    ,

    anchorType: isMulti ? 'button' : undefined,
    ButtonContent: use === 'button' ? ButtonContent : null,
    className: 'p-x-0',
    value
  });
  if (use === 'label') {
    return /*#__PURE__*/_jsx(UIInlineLabel, {
      label: I18n.text('frameworkListingLib.usersAndTeamsSelect.label'),
      children: selectComponent
    });
  }
  return /*#__PURE__*/_jsx(ListingLibFilterWrapper, {
    active: isMulti && Array.isArray(value) ? value.length > 0 : !Array.isArray(value) && value !== SELECTION_ID_ALL,
    onClearFilter: handleClearUsersAndTeams,
    children: selectComponent
  });
}